import { useCallback, useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { AxiosError } from 'axios';
import { useModal } from '@cbhq/cds-common/overlays/useModal';
import { ResponsiveProps } from '@cbhq/cds-web';
import { Button } from '@cbhq/cds-web/buttons';
import { RadioGroup } from '@cbhq/cds-web/controls';
import { Icon } from '@cbhq/cds-web/icons';
import { Box, VStack } from '@cbhq/cds-web/layout';
import { Modal, ModalBody, ModalHeader } from '@cbhq/cds-web/overlays';
import { useToast } from '@cbhq/cds-web/overlays/useToast';
import { TextBody, TextHeadline } from '@cbhq/cds-web/typography';

import { useIsCOMSWriteEnabled } from ':cloud/hooks/featureFlags/useIsCOMSWriteEnabled';
import { useSimpleBreakpoints } from ':cloud/hooks/useSimpleBreakpoints';
import { EventName, logClick } from ':cloud/init/clientAnalytics/logging';
import { useCreateAccountItem } from ':cloud/queries/BillingQueries/useCreateAccountItem';
import { useGetPaymentMethod } from ':cloud/queries/BillingQueries/useGetPaymentMethod';
import { useGetProducts } from ':cloud/queries/BillingQueries/useGetProducts';
import { useCreateProject } from ':cloud/queries/Projects/useCreateProject';
import { useGetProjects } from ':cloud/queries/Projects/useGetProjects';
import { BillingMeter, NewProjectFormContext } from ':cloud/types/ts_types';
import { generateStringValidationError } from ':cloud/utils/generateStringValidationError';
import { AppRoute } from ':cloud/utils/routes';
import { projectNameSchema } from ':cloud/utils/validate';
import { ConnectedProductsField } from ':cloud/widgets/waasProjects/projectFormFields/ConnectedProductsField';
import { FieldCopy } from ':cloud/widgets/waasProjects/projectFormFields/FieldCopy';
import { FreeCreditsDisclaimerField } from ':cloud/widgets/waasProjects/projectFormFields/FreeCreditsDisclaimerField';
import { PaymentMethodField } from ':cloud/widgets/waasProjects/projectFormFields/PaymentMethodField';
import { ProjectNameField } from ':cloud/widgets/waasProjects/projectFormFields/ProjectNameField';

const radioOptions = { yes: 'Yes', no: 'No' };

const responsiveConfigProjectName: ResponsiveProps = {
  phone: { spacingTop: 0, gap: 4 },
  tablet: { spacingTop: 8, gap: 9 },
  desktop: { spacingTop: 8, gap: 9 },
};

type NewProjectDetailsProps = {
  meter?: BillingMeter;
  unitType: string;
  onReplaceCardClick: React.MouseEventHandler<HTMLInputElement>;
  onCancelReplaceCard: React.MouseEventHandler<HTMLInputElement>;
  organizationId: string;
  saveFormContext: (formData: NewProjectFormContext) => void;
  formContext: NewProjectFormContext;
};

export function NewProjectDetails({
  meter,
  unitType,
  onReplaceCardClick,
  organizationId,
  saveFormContext,
  formContext,
}: NewProjectDetailsProps) {
  const history = useHistory();
  const toast = useToast();
  const { isPhone } = useSimpleBreakpoints();
  const isCOMSWriteEnabled = useIsCOMSWriteEnabled();
  const { projects } = useGetProjects(organizationId);
  const { products } = useGetProducts();
  const waasVariant = products[0]?.variants.find((variant) => variant.active);

  const { paymentMethod } = useGetPaymentMethod(organizationId);

  const createAccountItem = useCreateAccountItem(organizationId);
  const createProjectMutation = useCreateProject();
  const [projectName, setProjectName] = useState(formContext?.projectName || '');
  const [projectNameError, setProjectNameError] = useState(formContext?.projectNameError || '');
  const [sanctionedAffiliations, setSanctionedAffiliations] = useState<string | undefined>(
    formContext?.sanctionedAffiliations || undefined,
  );

  const handleOnChangeName = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    setProjectName(event.target.value.trim());
    setProjectNameError('');
  }, []);

  const handleOnChangeSanctionedAffiliations = useCallback(
    (value) => {
      saveFormContext({
        ...formContext,
        sanctionedAffiliations: value,
      });
      setSanctionedAffiliations(value);
    },
    [saveFormContext, formContext],
  );

  const handleOnBlur = useCallback(() => {
    const existingProjectNames = projects.map((p) => p.name);
    const nameError = generateStringValidationError(projectName, projectNameSchema);
    const duplicateNameError = existingProjectNames.includes(projectName)
      ? 'Project name already exists'
      : '';
    saveFormContext({
      ...formContext,
      projectName,
      projectNameError: nameError || duplicateNameError,
    });
    setProjectNameError(nameError || duplicateNameError);
  }, [projects, projectName, saveFormContext, formContext]);

  const isSubmitDisabled = sanctionedAffiliations !== 'no' || !!projectNameError || !projectName;

  const handleSubmitProject = useCallback(() => {
    const hasFormErrors = Boolean(!organizationId || !sanctionedAffiliations);

    logClick(EventName.create_project, {
      errors: hasFormErrors && JSON.stringify({ organizationId }),
      productType: 'waas',
    });

    if (hasFormErrors || !waasVariant) {
      // not sure what errors we want to show if the waasVariant doesn't exist?
      return;
    }
    createProjectMutation.mutate(
      { name: projectName, organizationId, products: ['waas'] },
      {
        onSuccess(data) {
          // @ts-expect-error -- @zack.wilder maintaining backwards compatibility until COMS FE cleanup
          const projectId = data?.id || data?.project?.id;
          if (!isCOMSWriteEnabled) {
            createAccountItem.mutate({
              projectId,
              productVariantId: waasVariant.id,
            });
          }

          localStorage.removeItem('waas-form-context');
          history.push(`${AppRoute.Projects.Home}/${projectId}`);
        },
        onError(error) {
          if (error instanceof AxiosError) {
            if (error.response?.data.message === 'project would exceed limit') {
              toast.show('You have reached the maximum number of projects allowed.', {
                variant: 'negative',
              });
            } else if (error.response?.data.message === 'project name already exists') {
              toast.show('Project name already exists, choose a different name.', {
                variant: 'negative',
              });
            } else {
              toast.show('Unable to create project. Please retry', {
                variant: 'negative',
              });
            }
          }
        },
      },
    );
  }, [
    organizationId,
    sanctionedAffiliations,
    waasVariant,
    createProjectMutation,
    projectName,
    isCOMSWriteEnabled,
    history,
    createAccountItem,
    toast,
  ]);

  const { openModal, closeModal } = useModal();
  const handleCloseModal = useCallback(() => {
    setSanctionedAffiliations(undefined);
    saveFormContext({
      ...formContext,
      sanctionedAffiliations: undefined,
    });
    closeModal();
  }, [closeModal, saveFormContext, formContext]);
  useEffect(() => {
    if (sanctionedAffiliations === 'yes') {
      openModal(
        <Modal
          visible
          onRequestClose={handleCloseModal}
          hideDividers
          width={isPhone ? undefined : 312}
        >
          <ModalHeader title="" />
          <ModalBody>
            <VStack alignItems="center" spacingVertical={5} spacingHorizontal={0.5} gap={2}>
              <Icon name="error" color="negative" size="l" />
              <TextHeadline as="p">You cannot create a project</TextHeadline>
              <TextBody as="p" align="center" color="foregroundMuted">
                Coinbase is prohibited from supporting anyone affiliated with sanctioned countries.
              </TextBody>
            </VStack>
            <Box spacingBottom={3}>
              <Button onPress={handleCloseModal} block>
                I understand
              </Button>
            </Box>
          </ModalBody>
        </Modal>,
      );
    }
  }, [sanctionedAffiliations, openModal, handleCloseModal, isPhone]);

  return (
    <VStack responsiveConfig={responsiveConfigProjectName} maxWidth={600}>
      <ProjectNameField
        handleOnChangeName={handleOnChangeName}
        handleOnBlur={handleOnBlur}
        projectName={projectName}
        projectNameError={projectNameError}
      />

      <ConnectedProductsField meter={meter} unitType={unitType} />

      <PaymentMethodField paymentMethod={paymentMethod} onReplaceCardClick={onReplaceCardClick} />

      <div>
        <FieldCopy
          title="International compliance"
          description="Does your company or project offer products or services, onboard customers, or receive payments from the following jurisdictions: North Korea, Syria, Iran, Cuba, Luhansk, Donetsk, or Crimea?"
        />
        <RadioGroup
          name="sanctionedAffiliations"
          value={sanctionedAffiliations}
          onChange={handleOnChangeSanctionedAffiliations}
          options={radioOptions}
          gap={0.5}
          direction="vertical"
        />
      </div>

      <Button
        onPress={handleSubmitProject}
        type="submit"
        loading={createProjectMutation.isLoading}
        name="Create project"
        disabled={isSubmitDisabled}
      >
        Create project
      </Button>

      <FreeCreditsDisclaimerField />
    </VStack>
  );
}
